import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Form, PerForm } from '@lbmx/per-form';
import { ToastrNotificationService } from '@lbmx/phoenix-lib-utils';
import { AppSetting, UserState } from '@lbmx/types';
import { TranslocoService } from '@ngneat/transloco';
import {
  createFeatureSelector,
  createSelector,
  select,
  Store,
} from '@ngrx/store';
import { LocaleSettings } from 'primeng';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { Calendar } from 'primeng/calendar';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ConfigProvider } from 'src/app/provider/config-provider';
import {
  ReportBuilderReportConfig,
  ReportTemplate,
  SingleQueryResponse,
} from '../flexmonster-loader/flexmonster-loader.ui';
import { buildLocale } from './calender-locale.ui';

@Component({
  selector: 'app-flexmonster',
  templateUrl: './flexmonster.component.html',
  styleUrls: ['./flexmonster.component.scss'],
})
export class FlexmonsterComponent implements OnInit, OnDestroy {
  @ViewChild('calendar') public calendarRef: Calendar;
  public appSettings: AppSetting = this.configPrv.AppSetting;
  public reportEndpoint = '';
  public form: PerForm;
  public params: Form;
  private _onDestroySubject = new Subject();
  public reportRequest: Observable<any>;
  public response: any;
  public groupedDataSources: SelectItemGroup[];
  public filteredDataSources: any[];
  public recordData: object[];
  public selectedReportTemp: ReportTemplate;
  public yearOptions: SelectItem[] = [];
  public selectedYear: number;
  public selectedDataSource: any;
  public rangeDates: Date | Date[] | null = null;
  public isDateAndReportSelected = false;
  public showChild = false;
  public minDate: Date;
  public maxDate: Date;
  public invalidDates: Date[];
  public selectionModeValue = 'range';
  public displayDateLabel = '';
  public dateRangeSelected = false;
  public locale: LocaleSettings;

  constructor(
    private configPrv: ConfigProvider,
    public httpClient: HttpClient,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private toast: ToastrNotificationService,
    private translocoService: TranslocoService,
    private store: Store<any>,
  ) {
    this.store
      .pipe(
        select(
          createSelector(
            createFeatureSelector<UserState>('user'),
            (state) => state.profile,
          ),
        ),
        tap((user) => {
          setTimeout(() => {
            this.locale = buildLocale({
              locale: user?.userInfo?.LanguageCode ?? navigator.language,
              clearLabel: this.translocoService.translate('ACTIONS.CLEAR'),
              todayLabel: this.translocoService.translate(
                'REPORTBUILDER.TODAY',
              ),
            });
          }, 0);
        }),
        takeUntil(this._onDestroySubject),
      )
      .subscribe();
  }

  public onDataSourceChange(event: any) {
    this.showChild = false;
    const selectedItem = this.getItemByValue(
      event.value,
      event.originalEvent.srcElement.innerText,
    );
    this.selectedDataSource = selectedItem;

    this.http
      .post<SingleQueryResponse>(
        this.configPrv.AppSetting.uriAnalytics.queryEndpoint +
          '/SearchByReportKey' || '',
        {
          ...selectedItem,
        },
        { withCredentials: true },
      )
      .subscribe((response) => {
        this.selectedReportTemp = { ...response.record };
        this.selectionModeValue = this.selectedReportTemp?.dateSelectionMode;
        this.displayDateLabel =
          this.selectionModeValue === 'single'
            ? 'REPORTBUILDER.DATE'
            : 'REPORTBUILDER.DATE_RANGE';
      });

    this.rangeDates = null;
    queueMicrotask(() => {
      this.showChild = true;
    });
  }

  private initializeYearOptions() {
    const currentYear = new Date().getFullYear();
    this.yearOptions = [];
    for (let i = 0; i < 3; i++) {
      this.yearOptions.push({
        label: `${currentYear - i}`,
        value: currentYear - i,
      });
    }
  }

  private EvaluateRangeDateAndReportSelected(dateLength: number): void {
    if (
      !Array.isArray(this.rangeDates) ||
      this.rangeDates.length !== dateLength
    ) {
      return;
    }

    const [startDate, endDate] = this.rangeDates;
    if (!(startDate && endDate) || !this.selectedReportTemp) {
      return;
    }

    this.isDateAndReportSelected = true;
    this.selectedReportTemp = { ...this.selectedReportTemp };
    if (this.calendarRef) {
      this.calendarRef.overlayVisible = false;
    }
  }

  private EvaluateSingleDateAndReportSelected(): void {
    if (!this.rangeDates) {
      return;
    }

    const first = Array.isArray(this.rangeDates)
      ? this.rangeDates[0]
      : this.rangeDates;

    const evalDate = first ? new Date(first) : null;
    if (!evalDate || !this.selectedReportTemp) {
      return;
    }

    this.isDateAndReportSelected = true;
    this.selectedReportTemp = { ...this.selectedReportTemp };
  }

  private handleDateChange() {
    if (this.selectionModeValue === 'range') {
      this.EvaluateRangeDateAndReportSelected(2);
    }

    if (this.selectionModeValue === 'single') {
      this.EvaluateSingleDateAndReportSelected();
    }
  }

  // Handle the Year Selection
  public onDateChange(event: any) {
    this.handleDateChange();
  }

  public getItemByValue(value: string, label: string) {
    for (const group of this.groupedDataSources) {
      const item = group.items.find(
        (i) => i.value === value && i.label === label,
      );
      if (item) {
        const report = {
          reportId: item.value,
          reportName: item.label,
          isGenerated: item['isGenerated'],
        };
        return report;
      }
    }
    return null;
  }

  public OnReportSaved(isSaved: boolean) {
    if (isSaved) {
      setTimeout(() => {
        this.PopulateDataSourceDropDown();
        this.cdr.detectChanges();
        this.toast.successNotify(
          this.translocoService.translate('REPORTBUILDER.TOAST_REPORT_SAVED'),
        );
      }, 400);
    }
  }
  public PopulateDataSourceDropDown() {
    this.http
      .get<ReportBuilderReportConfig>(
        this.configPrv.AppSetting.uriAnalytics.getReportBuilderDashboardConfig,
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          this.groupedDataSources = [...response.fields.report.options];
          this.groupedDataSources = [...this.groupedDataSources];
        }),
      )
      .subscribe();
  }
  public ngOnInit(): void {
    this.initializeYearOptions();
    this.PopulateDataSourceDropDown();
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = prevMonth === 11 ? year - 1 : year;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = nextMonth === 0 ? year + 1 : year;
    this.minDate = new Date();
    this.minDate.setMonth(prevMonth);
    this.minDate.setFullYear(prevYear);
    this.maxDate = new Date();
    this.maxDate.setMonth(nextMonth);
    this.maxDate.setFullYear(nextYear);

    this.maxDate = today; // Set maxDate to today's date
    this.minDate = new Date();
    this.minDate.setFullYear(today.getFullYear() - 3); // Set minDate to 3 years before today

    // Invalid dates (optional logic)
    const invalidDate = new Date();
    invalidDate.setDate(today.getDate() - 1);
    this.invalidDates = [today, invalidDate];
  }

  public ngOnDestroy(): void {
    this._onDestroySubject.next(true);
  }
}
